const AbstractRepository = require('@/lib/Repositories/AbstractRepository');
const ApiResponseUnmapper = require('@/libraries/DigiTickets/Api/ApiResponseUnmapper');
const DataStoreNames = require('@/lib/DataStores/DataStoreNames');
const DateList = require('@/lib/Dates/DateList');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const Session = require('@/lib/Sessions/Session');
const { basicHydrateArray } = require('@/lib/Hydration/basicHydrator');
const { toYMD } = require('@/functions/date');

class SessionRepository extends AbstractRepository {
    /**
     * @param {DataStore} dataStore
     * @param {Hydrator} hydrator
     * @param {SyncStateManager} syncStateManager
     * @param {ApiClient} apiClient
     * @param {AppConfig} appConfig
     * @param {UserService} userService
     */
    constructor(
        dataStore,
        hydrator,
        syncStateManager,
        apiClient,
        appConfig,
        userService
    ) {
        super(dataStore, hydrator, syncStateManager);

        this.apiClient = apiClient;
        this.appConfig = appConfig;
        this.userService = userService;

        /**
         * @type {ConsoleLogger}
         */
        this.log = logFactory('SessionRepository');

        /**
         * How frequently in ms is the API queried for updated sessions (in milliseconds)
         *
         * @type {number}
         */
        this.updateDelay = 5 * 60 * 1000; // 5 minutes

        this.updateInterval = null;
        this.startUpdateInterval();
    }

    clearDataBeforeInserting() {
        return false;
    }

    getEntityClass() {
        return Session;
    }

    getEntityName() {
        return 'Session';
    }

    getTableName() {
        return DataStoreNames.SESSIONS;
    }

    /**
     * Performs a full sync of sessions.
     * - Deletes all sessions from the data store.
     * - Fetches all the sessions from the API (in chunks).
     * - Stores all the results in the data store.
     * - Sets the last sync time.
     *
     * @param {number|null} [branchID] - The branch the data is requested for, where applicable.
     *
     * @return {Promise}
     */
    async sync(branchID = null) {
        this.log.debug('Syncing all sessions.');

        if (this.clearDataBeforeInserting()) {
            await this.clear();
        }

        const startedAt = new Date();
        return new Promise((resolve, reject) => {
            let sessionCount = 0;
            const savePromises = [];

            const onRequestFinished = async (res) => {
                // If PRO-1436 starts to cause too many performance issues due to too many sessions, instead of
                // reverting the changes a quick fix to go back to loading a fixed number of sessions is to
                // replace the below line with this:
                // const hasMorePages = false;
                // This will make it stop after loading the first page of sessions (10,000 at the time of writing).
                const hasMorePages = res.links && res.links.next;

                if (hasMorePages) {
                    // There are more pages of data to load - go get the next one,
                    // and call the onRequestFinished function again when done.
                    let url = new URL(res.links.next.url);
                    // The link header returned by the API is an absolute url but there are occasions where this is
                    // not what we want, for example when testing using Ngrok (see confluence), so create a new url.
                    let newUrl = this.appConfig.apiBaseUrl + url.pathname.substring(4) + url.search;
                    this.apiClient.requestUrl('GET', newUrl)
                        .then(onRequestFinished)
                        .catch(reject);
                }

                // While that next API request for the next page is running, save the data from the last request.
                // The setTimeout makes this block run slightly delayed so that the API request above fires off
                // immediately and this saving process happens concurrent to the next page's API request.
                setTimeout(() => {
                    savePromises.push(
                        this.processApiResponseData(res.data)
                            .then((count) => {
                                sessionCount += count;
                            })
                    );

                    if (!hasMorePages) {
                        // The last request was the last page.
                        // Wait for all saving to the DB promises to resolve, then resolve the overall sync promise.
                        Promise.all(savePromises)
                            .then(() => {
                                this.log.debug(
                                    'Synced all ' + sessionCount + ' sessions in ' + ((new Date()).getTime() - startedAt.getTime()) + 'ms.'
                                );

                                this.saveLastSync(branchID);

                                resolve();
                            })
                            .catch(reject);
                    }
                }, 1);
            };

            // Make the first request.
            this.fetchAllSessionDataFromApi(branchID)
                .then(onRequestFinished)
                .catch(reject);
        });
    }

    /**
     * Performs only an 'update sync' of sessions.
     * - Fetches only recently changed sessions from the API and stores them.
     * - Deletes any sessions where the status is no longer 'Active'.
     *
     * @return {Promise}
     */
    syncChanges() {
        this.log.debug('Updating sessions.');

        const branchID = this.userService.getItemBranchID();

        return this.fetchUpdatedSessionDataFromApi(branchID)
            .then(({ data }) => this.unmapApiResponseData(data))
            .then((unmappedData) => this.hydrateApiResponseData(unmappedData))
            .then((apiResult) => this.storeApiResult(apiResult))
            .then(() => this.saveLastSync(branchID));
    }

    /**
     * @return {?Date}
     */
    getLastSyncedAt() {
        const lastSync = this.syncStateManager.getLastSync(
            DataStoreNames.SESSIONS,
            this.userService.getItemBranchID(),
        );

        return lastSync ? lastSync.syncedAt : null;
    }

    /**
     * @private
     *
     * Make an API request to download sessions.
     * Returns a promise with a list of active sessions in the response and an array of non-active session IDs in
     * the response.
     *
     * @param {number} branchID
     *
     * @return {Promise<{data: *[], headers: {}}>} Returns raw (un-hydrated) data.
     */
    fetchAllSessionDataFromApi(branchID) {
        const queryParams = {
            branchID,
            limit: this.appConfig.maxSessionsToSyncPerRequest,
            status: 'Active,Inactive',
            // Get all sessions that start in the future, or since midnight today.
            // When displaying the session the session picker will filter out sessions that are no longer bookable,
            // but we want all of today's sessions for the today view.
            startingToday: 1,
        };

        return this.makeApiRequest(queryParams);
    }

    /**
     * Makes an API request to fetch only sessions that have been created or modified since the last sync.
     *
     * @param {number} branchID
     *
     * @return {Promise<{data: *[], headers: {}}>} Returns raw (un-hydrated) data.
     */
    fetchUpdatedSessionDataFromApi(branchID) {
        const lastSyncedAt = this.getLastSyncedAt();
        if (!lastSyncedAt) {
            return Promise.reject(new Error('Cannot fetch changes because there is no last sync date for sessions.'));
        }

        const queryParams = {
            branchID,
            status: 'Active,Inactive,Archived,Deleted',
            updatedSince: lastSyncedAt.toISOString(),
        };

        return this.makeApiRequest(queryParams);
    }

    /**
     * @param {*} queryParams
     *
     * @return {Promise<{data: *[], headers: {}}>}
     */
    makeApiRequest(queryParams) {
        return this.apiClient.request('GET', 'sessions/slim', queryParams);
    }

    /**
     * @private
     *
     * Accepts a raw object of data that came from the API and unmaps it, hydrates into Session objects,
     * and saves those object to IndexedDB (or deletes them from IndexedDB if necessary).
     *
     * @param data
     *
     * @return {Promise<Number>} Returns a promise with the number of sessions saved.
     */
    processApiResponseData(data) {
        // Unmap the data.
        data = this.unmapApiResponseData(data);

        // Hydrate into Session models and organise into new/deleted sessions.
        data = this.hydrateApiResponseData(data);

        // Save into IndexedDB.
        return this.storeApiResult(data)
            .then(() => data.saleableSessions.length + data.archivedAndDeletedSessionIDs.length);
    }

    unmapApiResponseData(responseData) {
        const startedAt = new Date();

        // The response from the /sessions/slim endpoint is a simple array of values.
        // Convert these to objects that the hydrator can handle.
        // See https://app.digitickets.test/build/api/private-doc/v2/index.html for the values in this response.
        const result = responseData.map((row) => ({
            ID: row[0],
            eventID: row[1],
            name: row[2],
            date: row[3],
            time: row[4],
            endDate: row[5],
            endTime: row[6],
            capacity: row[7],
            spacesAvailable: row[8],
            status: ApiResponseUnmapper.unmapStatus(row[9]),
            startsAt: row[10],
            endsAt: row[11],
            branchID: row[12],
        }));

        this.log.debug(
            'Unmapped ' + result.length + ' sessions in ' + ((new Date()).getTime() - startedAt.getTime()) + 'ms.'
        );

        return result;
    }

    /**
     * @private
     *
     * Takes a raw response from the API, hydrates Session models from it, and sorts them into active/inactive,
     * or archived/deleted.
     *
     * @param {[]} responseData
     *
     * @return {{saleableSessions:Session[], archivedAndDeletedSessionIDs:int[]}}
     */
    hydrateApiResponseData(responseData) {
        // This is the structure the promise will receive.
        // Archived and Deleted sessions are separated out and just their IDs are returned so we don't waste
        // time hydrating a Session that will never be used.
        let apiResult = {
            /**
             * Sessions with status Active or Inactive
             *
             * @type {Session[]}
             */
            saleableSessions: [],
            /**
             * Sessions with status Archived or Deleted
             *
             * @type {number[]}
             */
            archivedAndDeletedSessionIDs: [],
        };

        // At this point we could filter out non active sessions and then use the Hydrator's
        // hydrateArray method, but that would iterate over the array multiple times.
        // So we just iterate over the whole array once here and call hydrate individually while also
        // sorting the Active from the non-Active sessions.
        let hydrationStart = new Date();

        for (let i = 0; i < responseData.length; i++) {
            if (['Active', 'Inactive'].includes(responseData[i].status)) {
                apiResult.saleableSessions.push(
                    this.hydrator.hydrate(
                        responseData[i],
                        new Session()
                    )
                );
            } else {
                apiResult.archivedAndDeletedSessionIDs.push(
                    // As of PROP-327 this will always be called ID, even when coming from the API.
                    // The unmapApiResponseData function in SessionRepository will call the first array element 'ID'.
                    responseData[i].ID
                );
            }
        }

        let hydrationDuration = (new Date()).getTime() - hydrationStart.getTime();
        this.log.debug(
            'Hydrated ' + apiResult.saleableSessions.length + ' sessions in ' + hydrationDuration + 'ms.'
        );

        return apiResult;
    }

    /**
     * @private
     *
     * Store the results from the API in IndexedDB.
     *
     * @param {{saleableSessions:Session[], archivedAndDeletedSessionIDs:int[]}} apiResult
     *
     * @return {Promise}
     */
    storeApiResult(apiResult) {
        // Promise.all is okay here because the order of these operations does not matter.
        return Promise
            .all(
                [
                    this.deleteSessions(apiResult.archivedAndDeletedSessionIDs),
                    this.storeSessions(apiResult.saleableSessions),
                ]
            );
    }

    /**
     * Delete the specified sessions from IndexedDB.
     *
     * @param {number[]} sessionIDs
     *
     * @return {Promise}
     */
    deleteSessions(sessionIDs) {
        if (sessionIDs.length < 1) {
            return Promise.resolve();
        }

        let startedAt = new Date();
        return this.dataStore.removeMany(DataStoreNames.SESSIONS, sessionIDs).then(
            (res) => {
                let duration = (new Date()).getTime() - startedAt.getTime();
                this.log.debug('Deleted ' + sessionIDs.length + ' sessions from DB in ' + duration + 'ms.');
                return res;
            }
        );
    }

    /**
     * @param {Session[]} sessions
     *
     * @return {Promise}
     */
    storeSessions(sessions) {
        if (sessions.length < 1) {
            return Promise.resolve();
        }

        let startedAt = new Date();
        return this.dataStore.persistMany(DataStoreNames.SESSIONS, sessions).then(
            (res) => {
                let duration = (new Date()).getTime() - startedAt.getTime();
                this.log.debug('Persisted ' + sessions.length + ' sessions to DB in ' + duration + 'ms.');
                return res;
            }
        );
    }

    startUpdateInterval() {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
            this.updateInterval = null;
        }

        this.updateInterval = setInterval(
            () => this.syncChanges(),
            this.updateDelay
        );
    }

    /**
     * Returns an array of every unique date (YMD) that contains at least 1 session for the given event.
     *
     * @param {number} eventID
     * @param {number} branchID
     *
     * @return {Promise<DateList>}
     */
    getDaysForEvent(eventID, branchID) {
        const loadStartedAt = new Date();
        return this.dataStore
            .getStore(DataStoreNames.SESSIONS)
            .where('[branchID+eventID+startTimeYMD]')
            .between(
                [branchID, eventID, this.dataStore.dexieMinKey()],
                [branchID, eventID, this.dataStore.dexieMaxKey()]
            )
            .uniqueKeys()
            .then((results) => {
                const loadDurations = (new Date()).getTime() - loadStartedAt.getTime();
                this.log.debug(`Loaded dates for event ${eventID} for branch ${branchID} in ${loadDurations}ms.`);

                return new DateList(
                    results.map((result) => new Date(result[2]))
                );
            });
    }

    /**
     * Returns an array of every session for the given event that starts in the given month.
     *
     * @param {number} eventID
     * @param {string} month YYYY-MM string.
     * @param {number} branchID
     *
     * @returns {Promise<Session[]>}
     */
    getSessionsForEventForMonth(eventID, month, branchID) {
        const loadStartedAt = new Date();
        return this.dataStore
            .getStore(DataStoreNames.SESSIONS)
            .where(
                {
                    branchID,
                    eventID,
                    startTimeYM: month,
                }
            )
            .toArray()
            .then((sessionsData) => {
                const loadDuration = (new Date()).getTime() - loadStartedAt.getTime();
                this.log.debug('Loaded ' + sessionsData.length + ' sessions from DB in ' + loadDuration + 'ms.');

                // We have an array of objects of data with the correct keys for a Session object, but they need to be
                // converted back to Sessions. We don't need to do a full hydration again as we know the keys match
                // up. So just do a "basic hydration" for each one.
                const rehydrateStartedAt = new Date();

                const sessions = basicHydrateArray(sessionsData, () => new Session());

                const rehydrateDuration = (new Date()).getTime() - rehydrateStartedAt.getTime();
                this.log.debug('Rehydrated ' + sessions.length + ' sessions in ' + rehydrateDuration + 'ms.');

                return sessions;
            });
    }

    /**
     * Returns every session that starts on the given day.
     *
     * @param {Date} date
     * @param {number} branchID
     *
     * @returns {Promise<Session[]>}
     */
    getSessionsForDate(date, branchID) {
        let loadStartedAt = new Date();

        const ymdDate = toYMD(date);
        this.log.debug('Loading sessions for branch', branchID, 'for date', ymdDate);

        return this.dataStore
            .getStore(DataStoreNames.SESSIONS)
            .where(
                {
                    branchID,
                    startTimeYMD: ymdDate,
                }
            )
            .toArray()
            .then((sessionsData) => {
                let loadDuration = (new Date()).getTime() - loadStartedAt.getTime();
                this.log.debug('Loaded ' + sessionsData.length + ' sessions from DB in ' + loadDuration + 'ms.');

                // We have an array of objects of data with the correct keys for a Session object, but they need to be
                // converted back to Sessions. We don't need to do a full hydration again as we know the keys match
                // up. So just do a "basic hydration" for each one.
                let rehydrateStartedAt = new Date();
                let sessionModels = basicHydrateArray(sessionsData, () => new Session());

                let rehydrateDuration = (new Date()).getTime() - rehydrateStartedAt.getTime();
                this.log.debug('Rehydrated ' + sessionModels.length + ' sessions in ' + rehydrateDuration + 'ms.');

                return sessionModels;
            });
    }

    /**
     * Modify the number of spaces sold and available of a session stored in the DB.
     *
     * @param {number} sessionID
     * @param {number} numberOfNewSpacesSold
     */
    adjustSoldSpaces(sessionID, numberOfNewSpacesSold) {
        return this.dataStore.transaction(
            this.dataStore.TRANSACTION_MODE.READWRITE,
            [
                this.dataStore.getStore('sessions'),
            ],
            async () => {
                await this.find(sessionID)
                    .then(
                        /**
                         * @param {Session} session
                         */
                        (session) => {
                            session.spacesSold += numberOfNewSpacesSold;
                            session.spacesAvailable -= numberOfNewSpacesSold;
                            this.storeSessions([session]);
                        }
                    );
            }
        );
    }

    /**
     * Set the spacesAvailable, spacesSold, and spacesReserved properties of the given sessions from the data provided.
     * Updates the objects and also persists them in the DB with the new data.
     *
     * @param {Session[]} sessions
     * @param {Object.<number, SessionAvailability>} sessionAvailabilities
     *
     * @returns {Promise}
     */
    setSpacesOfSessions(sessions, sessionAvailabilities) {
        sessions.forEach((session) => {
            if (sessionAvailabilities.hasOwnProperty(session.ID)) {
                const oldSpacesAvailable = session.spacesAvailable;
                const oldSpacesSold = session.spacesSold;
                const oldSpacesReserved = session.spacesReserved;

                session.setSpacesFromAvailability(sessionAvailabilities[session.ID]);

                const hasChanges = session.spacesAvailable !== oldSpacesAvailable
                    || session.spacesSold !== oldSpacesSold
                    || session.spacesReserved !== oldSpacesReserved;

                if (hasChanges) {
                    this.log.debug(
                        `Updated spaces for session ${session.ID}.`
                        + ` spacesAvailable: ${oldSpacesAvailable} -> ${session.spacesAvailable}.`
                        + ` spacesSold: ${oldSpacesSold} -> ${session.spacesSold}.`
                        + ` spacesReserved: ${oldSpacesReserved} -> ${session.spacesReserved}.`
                    );
                }
            }
        });

        // Persist the changes to the local DB.
        return this.persistMany(sessions);
    }

    /**
     * Sets the 'session' property of each line in the given array of order/cart lines, where that session can be
     * found in the local store.
     *
     * @param {OrderLine[]|CartLine[]} lines
     */
    attachSessionsToOrderLines(lines) {
        return Promise.all(
            lines.map((line) => {
                if (!line.sessionID) {
                    return Promise.resolve();
                }

                return this.find(line.sessionID)
                    .then((session) => {
                        line.session = session;
                    })
                    .catch(() => {});
            })
        );
    }
}

module.exports = SessionRepository;
